<template>
    <v-card class="activity-offer-card my-2" :elevation="!fullCard ? 0 : undefined" variant="outlined">
        <v-list-item v-if="!infoLoading" lines="three">
            <div class="d-flex flex-column flex-md-row">
                <div class="activity-offer-card__content align-self-start" :class="{'pl-2': !fullCard}">
                    <div
                        class="activity-offer-card__name wrap-words d-flex align-center text-subtitle-1 font-weight-bold cursor-pointer mb-1"
                        @click="offerInfo()"
                    >
                        <v-icon size="small" start>mdi-information-outline</v-icon>
                        {{ offer.serviceName }}
                        <ProductOfferTipsDialog v-if="offer.tipsAvailable" :offer-key="offer.offerKey" />
                        <span v-if="offerTime" class="ms-2 text-grey font-weight-bold text-body-2">
                            {{ `(${offerTime})` }}
                        </span>
                    </div>
                    <div v-if="discountDetails" class="d-flex flex-shrink-0 width-max-content">
                        <special-offer-label :details="discountDetails" />
                    </div>
                    <div
                        v-if="offer.available"
                        class="activity-offer-card__available d-flex align-center text-caption wrap-words"
                    >
                        <v-icon :title="$t('available')" start size="small" class="text-success">
                            mdi-checkbox-marked-circle
                        </v-icon>
                        <span class="text-success" v-text="$t('available')" />
                    </div>
                    <div v-else class="activity-offer-card__available d-flex align-center text-caption wrap-words">
                        <v-icon start :title="$t('on_request')" size="small" class="text-warning"
                            >mdi-alert-circle</v-icon
                        >
                        <span class="text-warning" v-text="$t('on_request')" />
                    </div>
                    <div class="activity-offer-card__date d-flex align-center text-caption">
                        <v-icon size="small" start>mdi-calendar</v-icon>
                        {{ $filters.dateFormat(offer.date) }}
                    </div>
                    <span v-if="offer && offer.price && showPriceDuringSearch" class="text-caption">
                        <conditions-label
                            :offer-key="offer.offerKey"
                            :price="offer.price"
                            @conditions="showConditionsInfo"
                        />
                    </span>
                    <transfer-driver-languages
                        v-if="offer.languages"
                        :lang-codes="offer.languages.map(el => el.langCode)"
                    />
                </div>
                <v-spacer />
                <div class="offer-price align-self-end align-self-md-center d-flex flex-column align-center">
                    <slot name="price" :offer="offer" :offer-key="offer.offerKey">
                        <template v-if="!offer.soldOut">
                            <v-btn
                                :loading="infoLoading"
                                :disabled="infoLoading"
                                color="primary"
                                height="36px"
                                max-height="36px"
                                block
                                variant="flat"
                                size="small"
                                no-prefetch
                                data-cy="client-price"
                                @click="action(selectForBooking)"
                            >
                                {{ label($filters.price(offer.price)) }}
                            </v-btn>
                            <template v-if="showPriceDuringSearch">
                                <price-note :search-request="searchRequest" />
                                <commission-amount :commission="offer.price.commission" />
                                <net-amount :price="offer.price" :search-request="searchRequest" />
                                <discount-label :price="offer.price" class="d-inline-block" />
                            </template>
                        </template>
                        <sold-out-modal v-else small />
                    </slot>
                </div>
            </div>
        </v-list-item>
        <v-skeleton-loader v-else type="paragraph" height="150" />
    </v-card>
</template>

<script lang="ts">
    import {Component, Prop, Setup, Vue} from 'vue-facing-decorator'
    import CommissionAmount from '@/components/snippets/CommissionAmount.vue'
    import ActivityInfoModal from '@/components/activities/info/ActivityInfoModal.vue'
    import DiscountLabel from '~src/components/snippets/discountLabel.src'
    import ConditionsLabel from '~src/components/snippets/conditionsLabel.src'
    import {EventBus, SHOW_ACTIVITIES_INFO_MODAL, SHOW_ACTIVITY_CONDITIONS_INFO} from '@/utils/event-bus'
    import PriceNote from '@/components/snippets/PriceNote.vue'
    import NetAmount from '~src/components/snippets/NetAmount.vue'
    import SpecialOfferLabel from '~/components/hotels/search/offers/SpecialOfferLabel.vue'
    import ProductOfferTipsDialog from '~/components/modals/ProductOfferTipsDialog.vue'
    import SoldOutModal from '~src/components/snippets/SoldOutModal.vue'
    import TransferDriverLanguages from '@/components/snippets/TransferDriverLanguages.vue'

    @Component({
        components: {
            SoldOutModal,
            SpecialOfferLabel,
            PriceNote,
            CommissionAmount,
            DiscountLabel,
            ActivityInfoModal,
            ConditionsLabel,
            NetAmount,
            ProductOfferTipsDialog,
            TransferDriverLanguages,
        },
    })
    export class ActivityOfferCard extends Vue {
        @Prop() offer
        @Prop({default: true}) fullCard
        @Prop() groupedBy
        @Setup(() => useBookingPriceButton().action) action
        @Setup(() => useBookingPriceButton().label) label
        @Setup(() => useBookingPriceButton().showPriceDuringSearch) showPriceDuringSearch

        imgError = false
        info = {
            images: [],
        }
        infoLoading = false

        async mounted() {
            try {
                this.infoLoading = true
                this.info = await this.$api.activityInfo.get({offerKey: this.offer.offerKey})
                if (this.fullCard) {
                    this.$emit('info', this.info)
                }
                if (this.fullCard && this.info.images) {
                    this.$emit('images', this.info.images)
                }
                // eslint-disable-next-line no-empty
            } catch (e) {
            } finally {
                this.infoLoading = false
            }
        }

        onImgError() {
            if (this.mainImage.url) {
                this.imgError = true
            }
        }

        selectForBooking() {
            activitiesStore.ADD_TO_BASKET({info: this.info, offerKey: this.offer.offerKey})
            this.$router.push({
                name: 'activityBooking',
                query: {offerKey: this.offer.offerKey},
            })
        }

        offerInfo() {
            EventBus.$emit(SHOW_ACTIVITIES_INFO_MODAL, {offer: this.offer, info: this.info})
        }

        showConditionsInfo() {
            EventBus.$emit(SHOW_ACTIVITY_CONDITIONS_INFO, this.offer)
        }

        get offerTime() {
            if (this.offer.startTimes?.length) {
                return this.offer.startTimes[0]
            }

            return null
        }

        get mainImage() {
            let mainImage = this.info.images.find(image => image.mainImage) || {url: null}
            if (!mainImage.url && this.info.images.length > 0) {
                mainImage = this.info.images[0]
            }
            return mainImage
        }

        get searchRequest() {
            return activitiesStore.searchRequest
        }

        get isAgency() {
            return authStore.isAgency
        }

        get discountDetails() {
            return this.offer?.price?.discountDetails
        }
    }

    export default ActivityOfferCard
</script>

<style scoped lang="scss">
    @use 'vuetify/settings';
    @use 'sass:map';

    .offer-price {
        width: 20%;
    }

    @media #{map.get(settings.$display-breakpoints, 'xs')} {
        .offer-price {
            width: 25%;
        }
    }

    .wrap-words {
        white-space: normal;
    }
</style>
